import React, { useState } from "react";

import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  InputAdornment,
  IconButton,
} from "@mui/material";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { useLoginPage } from "./useLoginPage";

import {
  inputSx,
  buttonSx,
  dialogPaperSx,
} from "./loginStyles";

import logo from "../../assets/amawta_lg.png";

import "./Login.css";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    dni,
    password,
    loading,
    helperText,
    handleDniChange,
    handlePasswordChange,
    bannerItems,
    activeBannerItem,
    bannerIndex,
    fade,
    goToSlide,
    errorModalOpen,
    errorModalMessage,
    closeErrorModal,
    handleSubmit,
    tiempoBloqueo,
    intentosRestantes,
  } = useLoginPage();

  // Ahora el rojo de error solo se activará si el usuario está bloqueado por intentos
  const isLocked = tiempoBloqueo > 0;

  return (
    <Box className="login-page">
      <Box className="login-shell">

        {/* =====================
            PANEL IZQUIERDO
        ====================== */}

        <Box className="login-showcase">
          {activeBannerItem && (
            <Box
              className={`login-carousel-content ${fade ? "fade-in" : "fade-out"
                }`}
            >
              <Box className="login-showcase-media" >
                <Box className="login-showcase-media-box">
                  <img
                    src={activeBannerItem.image}
                    alt={activeBannerItem.title}
                    className="login-showcase-image"
                  />
                </Box>
              </Box>

              <Typography className="login-showcase-title">
                {activeBannerItem.title}
              </Typography>

              <Typography className="login-showcase-description">
                {activeBannerItem.description}
              </Typography>
            </Box>
          )}

          <Box className="login-carousel-dots">
            {bannerItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`login-dot ${bannerIndex === index ? "active" : ""
                  }`}
                onClick={() => goToSlide(index)}
                aria-label={`Ir a ${item.title}`}
              />
            ))}
          </Box>
        </Box>

        {/* =====================
            PANEL DERECHO
        ====================== */}

        <Box className="login-card">

          <img
            src={logo}
            alt="Logo AMAWTA"
            className="login-logo"
          />

          <Typography className="login-title">
            Iniciar Sesión
          </Typography>

          <form onSubmit={handleSubmit} className="login-form">

            <TextField
              fullWidth
              label="DNI"
              placeholder="Ingresa tu DNI (8 dígitos)"
              variant="standard"
              value={dni}
              error={isLocked} // Solo se pone rojo si está bloqueado
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^[0-9]+$/.test(value)) {
                  handleDniChange(e);
                }
              }}
              helperText={helperText}
              inputProps={{ maxLength: 8 }}
              InputProps={{
                readOnly: isLocked, // Bloquea escritura si hay castigo
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon
                      sx={{
                        color: isLocked ? "#d32f2f" : (dni.length > 0 ? "#02123c" : "#8a92a6"),
                        mb: 0.5
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...inputSx,
                "& .MuiInput-underline:before": {
                  borderBottomColor: isLocked ? "#d32f2f" : (dni.length > 0 ? "#02123c" : "#e5e7eb")
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: isLocked ? "#d32f2f" : "#02123c"
                }
              }}
            />

            <TextField
              fullWidth
              label="Contraseña"
              placeholder="Ingresa tu contraseña"
              variant="standard"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              InputProps={{
                readOnly: isLocked,
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      sx={{
                        color: isLocked ? "#d32f2f" : (password.length > 0 ? "#02123c" : "#8a92a6"),
                        mb: 0.5,
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: password ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={isLocked}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...inputSx,
                "& input::-ms-reveal, & input::-ms-clear": {
                  display: "none",
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: isLocked ? "#d32f2f" : (password.length > 0 ? "#02123c" : "#e5e7eb")
                },
                mt: 2,
                "& .MuiInput-underline:after": {
                  borderBottomColor: isLocked ? "#d32f2f" : "#02123c"
                }
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={
                loading ||
                dni.length !== 8 ||
                !password.trim() ||
                tiempoBloqueo > 0
              }
              className="login-button-active"
              sx={buttonSx}
            >
              {loading ? "Validando..." : "INICIAR SESIÓN"}
            </Button>
          </form>

          <Typography sx={{ mt: 1, width: '100%' }} className="login-copy" >
            © 2026 - Colaboraccion SAC
          </Typography>

        </Box>
      </Box>

      {/* =====================
          MODAL ERROR AMIGABLE
      ====================== */}
      {/* =====================
          MODAL ERROR REDISEÑADO (Estilo AMAWTA)
      ====================== */}
      <Dialog
        open={errorModalOpen}
        onClose={closeErrorModal}
        PaperProps={{
          sx: {
            ...dialogPaperSx, // Mantenemos tu estilo base (bordes redondeados)
          }
        }}
      >
        <DialogTitle sx={{
          color: "#02123c", // Azul oscuro institucional
          fontWeight: 700,
          fontSize: "1.3rem",
          pb: 1
        }}>
          Aviso de inicio de sesión
        </DialogTitle>

        <DialogContent pb={0}>
          <Typography sx={{
            color: "#4b5563",
            lineHeight: 1.6,
            fontSize: "1rem"

          }}>
            {"¡Hola! No hemos podido validar tus credenciales. Por favor, verifica tus datos e inténtalo nuevamente." || errorModalMessage}
          </Typography>

          {/* Advertencia de intentos en color Cian/Turquesa */}
          {tiempoBloqueo === 0 && intentosRestantes > 0 && (
            <Typography sx={{
              color: "#ba0600", // Color cian de la marca (un tono ligeramente más legible)
              fontWeight: 600,
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}>
              {/* Opcional: Icono pequeño para reforzar */}
              <span style={{ fontSize: '1rem' }}>⚠️</span>
              Te {intentosRestantes === 1 ? "queda" : "quedan"} {intentosRestantes} {intentosRestantes === 1 ? "intento" : "intentos"} antes del bloqueo temporal.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3 }}>
          {/* Botón sólido con el azul institucional */}
          <Button
            onClick={closeErrorModal}
            variant="contained"
            sx={{
              backgroundColor: "#02123c",
              color: "#ffffff",
              fontWeight: 600,
              px: 4,
              borderRadius: "8px",
              textTransform: "none", // Evita que se ponga todo en mayúsculas
              fontSize: "1rem",
              "&:hover": {
                backgroundColor: "#03206b", // Un azul un poco más claro al pasar el mouse
              }
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
